import { useState } from "react";
import { uniqueId } from "lodash-es";
import { useCallback } from "react";

import { AssignRoleFormEditFields, AssignRoleFormAddFields } from "./types";

type AssignedRole = {
  id: string;
  roleID: string;
  spaceID: string;
};

const useDefaultRoleAssignmentMethods = (initialValues: AssignedRole[] = []) => {
  const [assignedRoles, setAssignedRoles] = useState<AssignedRole[]>(initialValues);

  const onCreate = useCallback(
    (formData: AssignRoleFormAddFields) => {
      const newAssignedRoles = formData.roleIds.map((roleID) => ({
        roleID,
        spaceID: formData.space,
        id: uniqueId(),
      }));

      setAssignedRoles([
        ...assignedRoles,
        ...newAssignedRoles.filter(
          (newAssignedRole) =>
            !assignedRoles.find(
              (assignedRole) =>
                assignedRole.roleID === newAssignedRole.roleID &&
                assignedRole.spaceID === newAssignedRole.spaceID
            )
        ),
      ]);
    },
    [assignedRoles, setAssignedRoles]
  );

  const onEdit = useCallback(
    (formData: AssignRoleFormEditFields, roleAssignmentId: string) => {
      setAssignedRoles(
        assignedRoles.reduce(
          (
            acc: Array<{ id: string; roleID: string; spaceID: string }>,
            assignedRole: { id: string; roleID: string; spaceID: string }
          ) => {
            if (assignedRole.id === roleAssignmentId) {
              return [
                ...acc,
                { ...assignedRole, roleID: formData.roleId, spaceID: formData.space },
              ];
            } else if (
              assignedRole.roleID === formData.roleId &&
              assignedRole.spaceID === formData.space
            ) {
              return acc;
            } else {
              return [...acc, assignedRole];
            }
          },
          []
        )
      );
    },
    [assignedRoles, setAssignedRoles]
  );

  const onDelete = useCallback(
    (roleAssignmentId: string) => {
      setAssignedRoles(assignedRoles.filter((role) => role.id !== roleAssignmentId));
    },
    [assignedRoles, setAssignedRoles]
  );

  return {
    onDelete,
    onCreate,
    onEdit,
    assignedRoles,
  };
};

export default useDefaultRoleAssignmentMethods;
